/**
 * «Сменить план» — ОТДЕЛЬНЫЙ ЭКРАН с «Назад» (canon формы §1.5 manager v3,
 * НЕ модалка/инлайн — feedback_every_form_is_a_separate_screen).
 *
 * Сравнение тарифов из каталога GET /v1/subscriptions/plans (Старт/Резорт/
 * Сеть): цена за номер · мес, текущий план помечен. Реальная смена плана —
 * через провижн (POST /v1/subscriptions/provision) на выбранный план; бэк #469
 * идемпотентен. Кнопки снизу: «Назад» + (на каждой карточке) «Выбрать».
 */
import { Button, Card, Skeleton } from '@hsc/ui';
import type { Subscription, SubscriptionPlan } from '@hsc/types';
import { useProvisionSubscription, useSubscriptionPlans } from './useSubscription.js';
import { formatMoney } from './format.js';

/** code тарифа → русское имя (fallback, если бэк не отдал name.ru). */
const PLAN_NAMES: Record<string, string> = {
  basic: 'LovArt Старт',
  start: 'LovArt Старт',
  pro: 'LovArt Резорт',
  resort: 'LovArt Резорт',
  enterprise: 'LovArt Сеть',
  network: 'LovArt Сеть',
};

/** code каталога → provision plan-код (start/resort/network). */
function toProvisionPlan(code: string): 'start' | 'resort' | 'network' {
  if (code === 'pro' || code === 'resort') return 'resort';
  if (code === 'enterprise' || code === 'network') return 'network';
  return 'start';
}

function planName(p: SubscriptionPlan): string {
  return p.name?.ru ?? PLAN_NAMES[p.code] ?? p.code;
}

export interface ChangePlanScreenProps {
  current: Subscription;
  onBack: () => void;
}

export function ChangePlanScreen({ current, onBack }: ChangePlanScreenProps): JSX.Element {
  const plans = useSubscriptionPlans();
  const provision = useProvisionSubscription();

  const onPick = (p: SubscriptionPlan) => {
    provision.mutate(
      {
        plan: toProvisionPlan(p.code),
        room_count: current.room_count,
        auto_renew: current.auto_renew,
      },
      { onSuccess: onBack },
    );
  };

  return (
    <section data-testid="change-plan-screen">
      <div className="ta-pagehead bill-topbar">
        <div>
          <div className="ta-pagehead__crumbs">Финансы / Подписка / Смена плана</div>
          <h2 className="ta-pagehead__title">Сменить план</h2>
        </div>
      </div>

      <div className="bill-body">
        {plans.isLoading ? (
          <div className="bill-grid-2" data-testid="change-plan-skeleton">
            <Card>
              <Skeleton height={120} radius={6} />
            </Card>
            <Card>
              <Skeleton height={120} radius={6} />
            </Card>
          </div>
        ) : plans.isError || plans.data === undefined ? (
          <Card data-testid="change-plan-error">
            <p role="alert">Не удалось загрузить тарифы.</p>
            <Button variant="secondary" onClick={() => void plans.refetch()}>
              Повторить
            </Button>
          </Card>
        ) : (
          <ul className="bill-plans" data-testid="change-plan-list">
            {plans.data.plans.map((p) => {
              const isCurrent = toProvisionPlan(p.code) === toProvisionPlan(current.tier);
              return (
                <li key={p.code}>
                  <Card
                    className={`bill-plan-opt${isCurrent ? ' bill-plan-opt--current' : ''}`}
                    data-testid={`change-plan-${p.code}`}
                  >
                    <div className="bill-plan-opt__name">{planName(p)}</div>
                    <div className="bill-plan-opt__price">
                      {formatMoney(p.price_per_room_minor / 100, p.currency || 'RUB')}
                      <span className="bill-kpi__unit"> / номер · мес</span>
                    </div>
                    {isCurrent ? (
                      <span className="ta-chip ta-chip--done bill-plan-opt__badge">
                        текущий план
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        disabled={provision.isPending}
                        data-testid={`change-plan-pick-${p.code}`}
                        onClick={() => onPick(p)}
                      >
                        {provision.isPending ? 'Применяем…' : 'Выбрать'}
                      </Button>
                    )}
                  </Card>
                </li>
              );
            })}
          </ul>
        )}

        {provision.isError ? (
          <p role="alert" className="ta-error" data-testid="change-plan-pick-error">
            Не удалось сменить план. Попробуйте позже.
          </p>
        ) : null}

        <div className="bill-form-actions">
          <Button variant="secondary" data-testid="change-plan-back" onClick={onBack}>
            Назад
          </Button>
        </div>
      </div>
    </section>
  );
}
